import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { useBranchFreeze } from "@/lib/useBranchFreeze";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  TrendingDown, Plus, Search, MoreHorizontal, Pencil, Trash2, AlertCircle, Lock,
} from "lucide-react";
import { PAYMENT_MODES, formatMode } from "@/lib/paymentModes";

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return d; }
};

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  description: "",
  amount: "",
  mode: "cash",
  reference_no: "",
  disbursed_by: "",
  note: "",
};

const SOURCE_BADGE = {
  salary: { label: "Salary", color: "bg-[#EDE7F6] text-[#5E35B1]" },
  other_deduction: { label: "Employee Advance", color: "bg-[#FFF3E0] text-[#E65100]" },
  manual: { label: "Manual", color: "bg-[#E3F2FD] text-[#1565C0]" },
};

export default function AccountingExpensesPage() {
  const { user } = useAuth();
  const { allBranches, selectedSchool, selectedBranch } = useBranch();
  const { frozenBranch, lockedBranchId } = useBranchFreeze();
  const currency = user?.currency_symbol || "₱";
  const peso = (n) => `${currency}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [branchFilter, setBranchFilter] = useState(() => lockedBranchId || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mode, setMode] = useState("all");
  const [q, setQ] = useState("");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formBranchId, setFormBranchId] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const branchesForSelectedSchool = useMemo(
    () => allBranches.filter((b) => b.school_id === selectedSchool?.id && b.status === "approved"),
    [allBranches, selectedSchool?.id]
  );

  // Frozen branch users always operate within their assigned branch.
  const effBranchFilter = frozenBranch && lockedBranchId ? lockedBranchId : branchFilter;

  const reload = useCallback(async () => {
    if (!selectedSchool) { setData(null); return; }
    setLoading(true);
    try {
      const params = { school_id: selectedSchool.id };
      if (effBranchFilter !== "all") params.branch_id = effBranchFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (mode !== "all") params.mode = mode;
      if (q.trim()) params.q = q.trim();
      const { data: res } = await api.get("/accounting/expenses", { params });
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedSchool, effBranchFilter, dateFrom, dateTo, mode, q]);

  useEffect(() => { reload(); }, [reload]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormBranchId(selectedBranch?.id || branchesForSelectedSchool[0]?.id || "");
    setOpenForm(true);
  };

  const openEdit = (row) => {
    if (row.source !== "manual") {
      toast.info("This row is auto-generated. Edit it from its source module.");
      return;
    }
    setEditing(row);
    setForm({
      date: row.date || "",
      description: row.description || "",
      amount: String(row.amount || ""),
      mode: row.mode || "cash",
      reference_no: row.reference_no || "",
      disbursed_by: row.disbursed_by || "",
      note: row.note || "",
    });
    setFormBranchId(row.branch_id);
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formBranchId) { toast.error("Pick a branch"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setBusy(true);
    try {
      const body = {
        branch_id: formBranchId,
        date: form.date,
        description: form.description.trim(),
        amount: amt,
        mode: form.mode,
        reference_no: form.reference_no.trim(),
        disbursed_by: form.disbursed_by.trim(),
        note: form.note.trim(),
      };
      if (editing) {
        delete body.branch_id;
        await api.patch(`/accounting/manual-expense/${editing.id}`, body);
        toast.success("Expense updated");
      } else {
        await api.post("/accounting/manual-expense", body);
        toast.success("Expense added");
      }
      setOpenForm(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.source !== "manual") {
      toast.error("This row is auto-generated and cannot be deleted from here.");
      setDeleteTarget(null);
      return;
    }
    try {
      await api.delete(`/accounting/manual-expense/${deleteTarget.id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <TrendingDown className="w-7 h-7 text-brand-purple" />
          Expenses
        </h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Record and monitor school expenses and disbursements.</p>
      </div>

      {!selectedSchool ? (
        <NoSchoolCard />
      ) : (
        <>
          {/* Filters */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="expenses-filters">
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Field label="School">
                <Input value={selectedSchool.name} disabled className="rounded-xl h-10 bg-brand-purpleLight/30" />
              </Field>
              <Field label="Branch">
                <select value={effBranchFilter} onChange={(e) => setBranchFilter(e.target.value)} disabled={frozenBranch} title={frozenBranch ? "Locked to your assigned branch" : undefined} className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm disabled:bg-brand-purpleLight/30 disabled:cursor-not-allowed disabled:opacity-80" data-testid="expenses-filter-branch">
                  {!frozenBranch && <option value="all">All branches</option>}
                  {branchesForSelectedSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </Field>
              <Field label="Search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Description, ref no., disbursed by" className="rounded-xl h-10 pl-9 border-brand-purple/15" data-testid="expenses-filter-search" />
                </div>
              </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Date From">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl h-10 border-brand-purple/15" data-testid="expenses-filter-from" />
              </Field>
              <Field label="Date To">
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl h-10 border-brand-purple/15" data-testid="expenses-filter-to" />
              </Field>
              <Field label="Mode of Disbursement">
                <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full h-10 rounded-xl border border-brand-purple/15 bg-white px-3 text-sm" data-testid="expenses-filter-mode">
                  <option value="all">All modes</option>
                  {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </Field>
            </div>
          </section>

          {/* Expenses List */}
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden mb-4" data-testid="expenses-list-section">
            <header className="px-4 py-3 border-b border-brand-purple/10 flex items-center justify-between gap-2 flex-wrap">
              <h2 className="font-heading text-lg font-semibold">Expenses List</h2>
              <Button onClick={openAdd} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-10 px-5" data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Expense
              </Button>
            </header>
            {loading ? (
              <div className="p-6 text-brand-inkMuted">Loading...</div>
            ) : (data?.expenses || []).length === 0 ? (
              <div className="p-8 text-center text-brand-inkMuted text-sm" data-testid="no-expenses">
                No expenses recorded for this window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1F5FF] text-brand-inkMuted text-[11px]">
                    <tr>
                      <th className="text-left px-4 py-3 font-heading font-semibold tracking-wider">DATE</th>
                      <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">DESCRIPTION</th>
                      <th className="text-right px-3 py-3 font-heading font-semibold tracking-wider">AMOUNT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">MODE OF DISBURSEMENT</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">REFERENCE NO.</th>
                      <th className="text-left px-3 py-3 font-heading font-semibold tracking-wider">DISBURSED BY</th>
                      <th className="text-center px-3 py-3 font-heading font-semibold tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map((r) => {
                      const badge = SOURCE_BADGE[r.source] || SOURCE_BADGE.manual;
                      const isAuto = r.source !== "manual";
                      return (
                        <tr key={`${r.source}-${r.id}`} className="border-t border-brand-purple/5 hover:bg-[#FAFBFE]" data-testid={`expense-row-${r.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                          <td className="px-3 py-3 text-center">
                            <div className="inline-flex items-center gap-2 flex-wrap justify-center">
                              <span>{r.description}</span>
                              <span className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold">{peso(r.amount)}</td>
                          <td className="px-3 py-3">{formatMode(r.mode)}</td>
                          <td className="px-3 py-3 font-mono text-xs">{r.reference_no || "—"}</td>
                          <td className="px-3 py-3">{r.disbursed_by || "—"}</td>
                          <td className="px-3 py-3 text-center">
                            {isAuto ? (
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-inkMuted" title="Auto-generated row — manage from source"><Lock className="w-3.5 h-3.5" /></span>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-inkMuted hover:bg-brand-purpleLight" data-testid={`expense-actions-${r.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="rounded-2xl w-36 p-1.5">
                                  <DropdownMenuItem onClick={() => openEdit(r)} className="rounded-xl gap-2 cursor-pointer" data-testid={`edit-expense-${r.id}`}>
                                    <Pencil className="w-4 h-4" /> Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setDeleteTarget(r)} className="rounded-xl gap-2 cursor-pointer text-[#C62828] focus:text-[#C62828]" data-testid={`delete-expense-${r.id}`}>
                                    <Trash2 className="w-4 h-4" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="px-4 py-3 bg-[#F8FAFF] border-t border-brand-purple/10 flex justify-end">
              <span className="text-sm text-brand-inkMuted">Total Expenses:&nbsp;</span>
              <span className="text-sm font-semibold text-brand-purple" data-testid="total-expenses">
                {peso(data?.totals?.grand_total)}
              </span>
            </footer>
          </section>

          <div className="rounded-2xl border-2 border-brand-purple/30 bg-gradient-to-r from-[#EFF4FF] to-white p-5 flex justify-between items-center" data-testid="grand-total-expenses">
            <span className="text-xs uppercase tracking-[0.18em] text-brand-inkMuted font-heading">Grand Total Expenses</span>
            <span className="text-3xl font-heading font-bold text-brand-purple">{peso(data?.totals?.grand_total)}</span>
          </div>
        </>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={openForm} onOpenChange={(o) => { setOpenForm(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-lg" data-testid="manual-expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription>
              Record any expense or disbursement (utilities, supplies, repairs, etc.).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editing && (
              <Field label="Branch" required>
                <select required value={formBranchId} onChange={(e) => setFormBranchId(e.target.value)} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm" data-testid="manual-expense-branch">
                  <option value="">Select branch...</option>
                  {branchesForSelectedSchool.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </Field>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date" required>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="manual-expense-date" />
              </Field>
              <Field label="Amount" required>
                <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="manual-expense-amount" />
              </Field>
            </div>
            <Field label="Description" required>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="e.g. FOLDER LONG" data-testid="manual-expense-description" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Mode of Disbursement">
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm" data-testid="manual-expense-mode">
                  {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </Field>
              <Field label="Reference No.">
                <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" data-testid="manual-expense-reference" />
              </Field>
            </div>
            <Field label="Disbursed By">
              <Input value={form.disbursed_by} onChange={(e) => setForm({ ...form, disbursed_by: e.target.value })} className="rounded-2xl h-11 border-brand-purple/20" placeholder="Who paid this out" data-testid="manual-expense-disbursed-by" />
            </Field>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="manual-expense-submit">
                {busy ? "Saving..." : editing ? "Save changes" : "Add Expense"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="delete-manual-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.description}" — {peso(deleteTarget?.amount)} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-delete-expense">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="font-heading text-sm">{label} {required && <span className="text-[#C62828]">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NoSchoolCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-school-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a school first</p>
      <p className="text-brand-inkMuted mt-1 text-sm">
        Accounting is scoped per school. Use the school/branch dropdown in the header.
      </p>
    </div>
  );
}
